import {useAppSelector} from "../../../../../../redux/hooks";
import {
  getMySongsPageSelectedSongDetailsLessonFavoriteStateByLessonId,
  getMySongsPageSelectedSongDetailsLessonLibraryStateByLessonId,
  getMySongsPageSelectedSongDetailsLessonPurchasedStateByLessonId,
} from "../../../../modules/my-songs-page-store/mySongsPage.selectors";
import {songLesson} from "../../../../../../services/songs/songLesson";
import { useMemo } from "react";
import {useSongDetailsDisplaySelectedLesson} from "../../hooks/useSongDetailsDisplaySelectedLesson";

interface ILibraryActionButtonLogic {
  buttonLabel: string;
  buttonAriaLabel: string;
  isButtonDisabled?: boolean;
  buttonActionHandler: () => void;
}

const libraryActionButtonLogicFactory = ({isPurchased, isInUserLibrary, lessonId}: {isInUserLibrary: boolean, isPurchased: boolean, lessonId: number}): ILibraryActionButtonLogic => {

  if (isPurchased) {
    return {
      isButtonDisabled: true,
      buttonLabel: `Purchased`,
      buttonAriaLabel: `This arrangement has been purchased`,
      buttonActionHandler: () => {},
    };
  }

  if (isInUserLibrary) {
    return {
      isButtonDisabled: false,
      buttonLabel: `Remove from Library`,
      buttonAriaLabel: `Remove this arrangement from your library`,
      buttonActionHandler: () => songLesson.removeFromUserLibrary(lessonId),
    };
  }

  // not purchased or in user library
  return {
    isButtonDisabled: false,
    buttonLabel: `Add to Library`,
    buttonAriaLabel: `Add this arrangement to your library`,
    buttonActionHandler: () => songLesson.addToLibrary(lessonId),
  };

}

const favoriteActionHandlerFactory = ({isFavorited, isInUserLibrary, lessonId}: {isInUserLibrary: boolean, isFavorited: boolean; lessonId: number}) => {
  if (isFavorited) {
    return () => songLesson.removeFavoriteExistingLibraryLesson(lessonId);
  }

  if (isInUserLibrary) {
    // will favorite lesson in user library
    return () => songLesson.favoriteExistingLibraryLesson(lessonId);
  }

  // will add to user library and favorite
  return () => songLesson.addToLibraryAndFavorite(lessonId);
}

export interface ISongDetailsLessonSongLibraryActionLogic extends ILibraryActionButtonLogic {
  isFavorited: boolean;
  favoriteActionHandler: () => void;
}

export const useSongDetailsLessonSongLibraryActionLogic = (): ISongDetailsLessonSongLibraryActionLogic => {
  const {lessonId: routerLessonId, firstLessonId} = useSongDetailsDisplaySelectedLesson();

  const lessonId = routerLessonId ?? firstLessonId ?? 0;

  const isInUserLibrary = !!useAppSelector((store) => getMySongsPageSelectedSongDetailsLessonLibraryStateByLessonId(store, lessonId));
  const isPurchased = !!useAppSelector((store) => getMySongsPageSelectedSongDetailsLessonPurchasedStateByLessonId(store, lessonId));
  const isFavorited = !!useAppSelector((store) => getMySongsPageSelectedSongDetailsLessonFavoriteStateByLessonId(store, lessonId));

  const {buttonLabel, buttonAriaLabel, isButtonDisabled, buttonActionHandler} = useMemo(
    () => libraryActionButtonLogicFactory({lessonId, isInUserLibrary, isPurchased}),
    [lessonId, isInUserLibrary, isPurchased]
  );

  const favoriteActionHandler = useMemo(
    () => favoriteActionHandlerFactory({isFavorited, isInUserLibrary, lessonId}),
    [isFavorited, isInUserLibrary, lessonId]
  );

  return {
    buttonLabel,
    buttonAriaLabel,
    isButtonDisabled,
    buttonActionHandler,

    isFavorited,
    favoriteActionHandler,
  };
}
